import styled from '@emotion/styled';
import { DirectorUser } from '@kaizenplatform/hugot-entity';
import { Avatar, Divider, Menu, Typography } from '@material-ui/core';
import AccountCircle from '@material-ui/icons/AccountCircle';
import ExitToApp from '@material-ui/icons/ExitToApp';
import _OpenInNew from '@material-ui/icons/OpenInNew';
import Settings from '@material-ui/icons/Settings';
import { makeStyles } from '@material-ui/styles';
import { createElement, FC, MouseEvent, MouseEventHandler, useState } from 'react';

import { OrganizationDetailFragment } from '../../../../generated/graphql';
import { itemBackground } from '../../../constant/color';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import OrganizationSelector from './OrganizationSelector';
import UserPopupMenuItem from './UserPopupMenuItem';

interface Props {
  signedInUser: DirectorUser;
  onClickSettings: MouseEventHandler<HTMLElement>;
  onClickSignOut: MouseEventHandler<HTMLElement>;
  onClickAccountSettings: MouseEventHandler<HTMLElement>;
  availableOrganizations: Array<OrganizationDetailFragment>;
  selectedOrganization: OrganizationDetailFragment | null;
  onChangeSelectedOrganization: (organization: OrganizationDetailFragment) => void;
  className?: string;
}

const UserPopupMenu: FC<Props> = ({
  signedInUser,
  onClickSettings,
  onClickSignOut,
  onClickAccountSettings,
  availableOrganizations,
  selectedOrganization,
  onChangeSelectedOrganization,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);

  const classes = makeStyles({
    popupStyle: {
      top: 'auto !important',
      bottom: '16px !important',
    },
  })();

  const handleClickMenu = (e: MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const handleRequestClose = () => {
    setOpen(false);
  };

  const handleClickSettings = (e: MouseEvent<HTMLElement>) => {
    onClickSettings(e);
    setOpen(false);
  };

  const handleClickAccountSettings = (e: MouseEvent<HTMLElement>) => {
    onClickAccountSettings(e);
    setOpen(false);
  };

  const handleClickSignOut = (e: MouseEvent<HTMLElement>) => {
    onClickSignOut(e);
    setOpen(false);
  };

  return (
    <div className={className}>
      <AvatarContainer onClick={handleClickMenu}>
        {signedInUser.profileImageUrl ? (
          <Avatar src={signedInUser.profileImageUrl} alt={signedInUser.name} style={open ? AvatarFill : AvatarStyle} />
        ) : (
          <AccountCircle color="action" style={open ? AvatarFill : AvatarStyle} />
        )}
      </AvatarContainer>
      <StyledMenu
        id="menu-appbar"
        anchorEl={anchorEl}
        classes={{ paper: classes.popupStyle }}
        open={open}
        onClose={handleRequestClose}
      >
        <AvatarWithName>
          <AccountIcon />
          <div>
            <Name variant="body2">{signedInUser.name}</Name>
            <Mail variant="caption">{signedInUser.email}</Mail>
          </div>
        </AvatarWithName>

        {availableOrganizations.length >= 1 && selectedOrganization && (
          <StyledChooseOrganization
            selectedOrganization={selectedOrganization}
            availableOrganizations={availableOrganizations}
            onChange={onChangeSelectedOrganization}
          />
        )}

        <Divider />

        <UserPopupMenuItem
          startIcon={<AccountCircle />}
          onClick={handleClickAccountSettings}
          labelMessageId={'sidebar.account_settings'}
          endIcon={<OpenInNew />}
        />
        <UserPopupMenuItem startIcon={<Settings />} onClick={handleClickSettings} labelMessageId={'sidebar.settings'} />
        <UserPopupMenuItem startIcon={<ExitToApp />} onClick={handleClickSignOut} labelMessageId={'sidebar.sign_out'} />
      </StyledMenu>
    </div>
  );
};

const AvatarContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  cursor: 'pointer',
  width: '100%',
  borderRadius: '4px',
  '&:hover': {
    background: itemBackground,
    transition: 'background 0.3s ease',
  },
});

const AvatarStyle = {
  width: 32,
  height: 32,
  color: '#4C566C',
};

const AvatarFill = {
  width: 32,
  height: 32,
  color: '#1DA772',
};

const StyledMenu = styled(Menu)({
  position: 'fixed',
  left: '90px !important',
  bottom: '0 !important',
  marginTop: SPACING_LARGE,
});

const AvatarWithName = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  {
    padding: `${SPACING_UNIT}px ${SPACING_REGULAR}px`,
    outline: 'none',
  },
);

const AccountIcon = styled(AccountCircle)({
  marginRight: SPACING_REGULAR,
  opacity: 0.5,
});

const Name = styled(Typography)({});

const Mail = styled(Typography)({});

const StyledChooseOrganization = styled(OrganizationSelector)({
  cursor: 'auto',
  marginBottom: SPACING_REGULAR,
  padding: `0 ${SPACING_REGULAR}px`,
  width: '100%',
  outline: 'none',
});

const OpenInNew = styled(_OpenInNew)({
  color: 'rgba(0, 0, 0, 0.54)',
});

export default UserPopupMenu;
